import { BaseNodeCommand, type NodeChangeRecord, type INodeMutationManager } from './base/BaseNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { computeDockedLayoutUpdatesForTable } from '../../domains/diagram/shapes/table/tableDockLayout.utils';
import type { Node } from '../models/scene';
import { SessionMetrics, TABLE_SESSION_METRICS } from '../../domains/analytics';

const MIN_CELL = 20;

const clampSize = (value: number) => (Number.isFinite(value) ? Math.max(MIN_CELL, value) : MIN_CELL);

const sum = (arr: number[]) => arr.reduce((total, value) => total + value, 0);

export interface ResizeTableGridParams {
  tableId: string;
  rowHeights: number[];
  columnWidths: number[];
  headerHeight: number;
  width?: number;
  height?: number;
}

type BatchMutationManager = INodeMutationManager & {
  applyBatchNodeChanges?: (updates: Array<{ nodeId: string; changes: Partial<Node> }>) => NodeChangeRecord[] | undefined;
};

export class ResizeTableGridCommand extends BaseNodeCommand {
  constructor(private params: ResizeTableGridParams) {
    super('Resize Table Grid');
  }

  protected doExecute(): NodeChangeRecord[] {
    const mutationManager = this.getMutationManager() as BatchMutationManager;
    const node = mutationManager.getNode(this.params.tableId);
    if (!node || node.type !== 'table') return [];

    const attrs = (node.attrs ?? {}) as Record<string, unknown>;
    const showHeader = attrs.showHeader !== false;

    const nextRowHeights = this.params.rowHeights.map((value) => clampSize(value));
    const nextColWidths = this.params.columnWidths.map((value) => clampSize(value));
    const nextHeaderHeight = showHeader ? clampSize(this.params.headerHeight) : 0;

    const nextWidth = Number.isFinite(this.params.width) ? (this.params.width as number) : sum(nextColWidths);
    const nextHeight =
      Number.isFinite(this.params.height) ? (this.params.height as number) : sum(nextRowHeights) + (showHeader ? nextHeaderHeight : 0);

    const changes = {
      width: nextWidth,
      w: nextWidth,
      height: nextHeight,
      h: nextHeight,
      attrs: {
        columnWidths: nextColWidths,
        rowHeights: nextRowHeights,
        headerHeight: nextHeaderHeight,
      },
    };

    const previewAttrs = { ...attrs, ...(changes.attrs as Record<string, unknown>) };
    const preview = { ...node, ...changes, attrs: previewAttrs } as Node;
    const dockedUpdates = computeDockedLayoutUpdatesForTable(preview, useDiagramStore.getState().nodes);

    if (dockedUpdates.length > 0 && typeof mutationManager.applyBatchNodeChanges === 'function') {
      const records = mutationManager.applyBatchNodeChanges([{ nodeId: this.params.tableId, changes }, ...dockedUpdates]) ?? [];
      if (records.length > 0) {
        SessionMetrics.increment(TABLE_SESSION_METRICS.RESIZES);
      }
      return records;
    }

    const record = mutationManager.applyNodeChange(this.params.tableId, changes);
    if (record) {
      SessionMetrics.increment(TABLE_SESSION_METRICS.RESIZES);
      return [record];
    }
    return [];
  }
}
